import { apiClient, API_BASE_URL } from './api.client';
import { getAppContext } from '../hooks/useAppContext';

/**
 * Preferences, kept on the server.
 *
 * Nothing the application remembers about a person lives in the browser any more: not which
 * company they last worked in, not whether a list shows as grid or table, not the sidebar, not the
 * theme. Each is a row on the server, loaded in one request at boot and held in memory for the page.
 * A choice made in one browser is there in the next; a browser wiped clean forgets nothing.
 *
 * Reads are synchronous against the in-memory copy, so a component can ask during render exactly
 * as it asked localStorage. Writes update the copy at once and reach the server on a short debounce
 * per key, so a slider dragged across ten values sends one request, not ten.
 *
 * Three scopes. USER: this person in this company (list views, sidebar, filters). GLOBAL: this
 * person in any company (the company they last worked in). COMPANY: everyone in this company (the
 * Payroll Settings sections, which used to live per-browser under prs_* and let two administrators
 * run payroll against different settings without either knowing).
 */
export type PreferenceScope = 'user' | 'global' | 'company';

interface Bundle { global: Record<string, string>; user: Record<string, string>; company: Record<string, string> }

const memory: Bundle = { global: {}, user: {}, company: {} };
let loaded = false;
let loading: Promise<void> | null = null;
const listeners = new Set<() => void>();
const pending = new Map<string, ReturnType<typeof setTimeout>>();

const WRITE_DEBOUNCE_MS = 400;

function bucket(scope: PreferenceScope): Record<string, string> {
  return scope === 'global' ? memory.global : scope === 'company' ? memory.company : memory.user;
}

/**
 * Load everything for the signed-in user and active company. Idempotent and shared: concurrent
 * callers wait on one request. Called at boot, after login, and again after a company switch —
 * the USER and COMPANY buckets belong to the company, so a switch replaces them.
 */
export function loadPreferences(force = false): Promise<void> {
  if (loaded && !force) return Promise.resolve();
  if (loading) return loading;
  // Anything still sitting on the write debounce goes FIRST. This load replaces the buckets
  // wholesale with the server's copy, and a queued write reads its value out of those buckets when
  // its timer finally fires — so a reload landing in that gap does not merely lose the change, it
  // sends the older value back to the server as if the user had chosen it. That is what made a
  // company switch survive until the next refresh and then quietly revert: the switch writes the
  // context, then immediately force-reloads to pick up the new company's preferences.
  // A FORCED reload means the company changed, and the USER and COMPANY buckets belong to the
  // company being left. Held onto until the new ones arrive, they are read as though they described
  // where we are now — and branding lives in the company bucket, with the company's own name in
  // shortName, which the brand renders ahead of everything. So the sidebar showed the PREVIOUS
  // company's name for as long as the fetch took, on every switch. Dropping them first means a
  // consumer reads nothing rather than something wrong, and the brand falls through to the company
  // name the session already carries.
  if (force) { memory.user = {}; memory.company = {}; listeners.forEach(l => l()); }

  loading = (pending.size > 0 ? flushPreferences() : Promise.resolve())
    .then(() => apiClient.get<Bundle>('/UserPreferences'))
    .then(res => {
      const b = res.data ?? { global: {}, user: {}, company: {} };
      memory.global = b.global ?? {};
      memory.user = b.user ?? {};
      memory.company = b.company ?? {};
      loaded = true;
      listeners.forEach(l => l());
    })
    .catch(() => {
      // Nothing to show yet is not a reason to stop the app: defaults apply until the server
      // answers, and the next load tries again.
    })
    .finally(() => { loading = null; });
  return loading;
}

export function preferencesLoaded(): boolean { return loaded; }

/** Forget everything — logout, or a session that turned out not to be ours. */
export function clearPreferences(): void {
  memory.global = {}; memory.user = {}; memory.company = {};
  loaded = false;
  pending.forEach(t => clearTimeout(t));
  pending.clear();
  listeners.forEach(l => l());
}

/** Re-run after a change, so a component can follow a preference it did not itself set. */
export function onPreferencesChanged(listener: () => void): () => void {
  listeners.add(listener);
  return () => { listeners.delete(listener); };
}

/** Every key in a scope beginning with `prefix` — for stores that keep one row per record. */
export function listPrefKeys(prefix: string, scope: PreferenceScope = 'user'): string[] {
  return Object.keys(bucket(scope)).filter(k => k.startsWith(prefix));
}

/** Synchronous read. `fallback` when the key is absent or unreadable. */
export function getPref<T>(key: string, fallback: T, scope: PreferenceScope = 'user'): T {
  const raw = bucket(scope)[key];
  if (raw === undefined) return fallback;
  try { return JSON.parse(raw) as T; } catch { return fallback; }
}

/**
 * Write. The in-memory copy changes immediately; the server receives it after a short debounce per
 * key. `undefined` removes the key.
 */
/**
 * Hold a value the SERVER already owns (organisation settings, branding, the favicon) so the
 * synchronous readers see it — without writing it back. The server is the source; a preference row
 * for it would be a copy, and a copy that everyone's browser tries to PUT on every load.
 */
export function primePref<T>(key: string, value: T | undefined, scope: PreferenceScope = 'user'): void {
  const b = bucket(scope);
  const next = value === undefined ? undefined : JSON.stringify(value);
  if (b[key] === next) return;
  if (next === undefined) delete b[key]; else b[key] = next;
  listeners.forEach(l => l());
}

export function setPref<T>(key: string, value: T | undefined, scope: PreferenceScope = 'user'): void {
  const b = bucket(scope);
  if (value === undefined) delete b[key];
  else b[key] = JSON.stringify(value);
  listeners.forEach(l => l());

  const id = `${scope}:${key}`;
  const prior = pending.get(id);
  if (prior) clearTimeout(prior);
  pending.set(id, setTimeout(() => {
    pending.delete(id);
    const path = scope === 'company'
      ? `/UserPreferences/company/${encodeURIComponent(key)}`
      : `/UserPreferences/${encodeURIComponent(key)}?scope=${scope}`;
    const req = value === undefined
      ? apiClient.delete(path)
      : apiClient.put(path, { valueJson: JSON.stringify(value) });
    req.catch(() => { /* the in-memory copy stands for this page; the next write retries */ });
  }, WRITE_DEBOUNCE_MS));
}

/**
 * Send every debounced write now — before a navigation that will unload the page.
 *
 * Sent with keepalive, so a reload that follows does not cancel them: the mode switch clears the
 * branch and reloads, and a write the reload cancelled came back from the server as the branch
 * the other mode owns. Resolves when the server has them, for callers that would rather wait.
 */
export function flushPreferences(): Promise<void> {
  const sends: Promise<unknown>[] = [];
  pending.forEach((t, id) => {
    clearTimeout(t);
    pending.delete(id);
    const [scope, ...rest] = id.split(':');
    const key = rest.join(':');
    const raw = bucket(scope as PreferenceScope)[key];
    const path = scope === 'company'
      ? `/UserPreferences/company/${encodeURIComponent(key)}`
      : `/UserPreferences/${encodeURIComponent(key)}?scope=${scope}`;
    // THE SAME CONTEXT HEADERS THE AXIOS INTERCEPTOR SENDS.
    //
    // This is a raw fetch because it needs keepalive — a reload must not cancel it — so nothing
    // adds them for it. It sent only X-Company-Id, and the server REQUIRES X-Branch-Id from every
    // role that is not global. So the one write that had to survive a reload was the one refused
    // with 400: picking a branch updated memory, the flush was rejected, the page reloaded, and
    // /auth/me answered with the branch still saved on the server — the switch undoing itself every
    // time. Owner and SuperAdmin are exempt from that requirement, which is why switching worked
    // for ElectEmb and for nobody else.
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    const ctx = getAppContext();
    if (ctx.companyId) headers['X-Company-Id'] = String(ctx.companyId);
    if (ctx.branchId) headers['X-Branch-Id'] = String(ctx.branchId);
    if (ctx.financialYearId) headers['X-Financial-Year-Id'] = String(ctx.financialYearId);
    sends.push(fetch(`${API_BASE_URL}${path}`, {
      method: raw === undefined ? 'DELETE' : 'PUT',
      headers,
      credentials: 'include',
      keepalive: true,
      body: raw === undefined ? undefined : JSON.stringify({ valueJson: raw }),
    }).catch(() => { /* best effort on the way out */ }));
  });
  return Promise.all(sends).then(() => undefined);
}
